/**
 * Cover-art facts about a song.
 *
 * A song's artwork can be in one of three conditions — an uploaded/real image, a
 * generated placeholder, or the default stand-in — and six surfaces have to agree
 * on which: library rows, the Studio's library strip, the ACTIVE SONG panel, the
 * Cover Art preview, its caption, and the public share page. The rules live here so
 * they are answered once.
 *
 * WHY artworkSeedFor EXISTS
 * -------------------------
 * The library used to seed its gradient on `song.id` while the Studio seeded on
 * `song.artworkSeed`. Both looked correct in isolation, and the result was that
 * generating cover art in the Studio changed the Studio's artwork and left the
 * library showing the old gradient — two unrelated covers for one song. Resolving
 * the seed through one function is what makes "the artwork" a property of the song
 * rather than of whichever component is drawing it.
 *
 * Pure functions on the domain model, no rendering. Which artwork to *draw* is
 * components/studio/CoverArt.tsx.
 */
import type { Song } from '../types/domain';

/**
 * The gradient seed for a song, whatever state its artwork is in.
 *
 * Falls back to the id so a song with no artwork still gets a stable colour rather
 * than an empty box — that fallback is the library's long-standing behaviour and is
 * what makes the default state look deliberate. Applying artwork replaces the seed,
 * and because every surface reads it from here, they all change together.
 */
export function artworkSeedFor(song: Song): string {
  return song.artworkSeed || song.id;
}

/**
 * True when artwork has been deliberately applied — generated or uploaded.
 *
 * Not the same as "there is something to draw": every song has a gradient, since
 * artworkSeedFor falls back to the id. This is the question the Cover Art preview's
 * empty state and the ACTIVE SONG panel's dashed border ask, and the answer has to
 * be no until the user applies something.
 */
export function hasCoverArt(song: Song): boolean {
  return Boolean(song.coverUrl || song.artworkSeed);
}

/**
 * True when the artwork is the seeded gradient rather than a real image.
 *
 * Drives the "Placeholder artwork" caption. Kept as its own question rather than
 * inlining `!song.coverUrl`, because that expression is also true when there is
 * no artwork at all, which is a different thing to say.
 */
export function isPlaceholderArt(song: Song): boolean {
  return !song.coverUrl && Boolean(song.artworkSeed);
}

/** Human label for where the current artwork came from. */
export function coverSourceLabel(song: Song): string {
  if (song.coverSource === 'uploaded') return 'Uploaded image';
  if (song.coverUrl) return 'Generated image';
  return 'Placeholder artwork';
}
